import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  Sparkles, ArrowRight, Users, Gift, Copy, Check, Share2, 
  ChevronUp, Star, Zap, Crown
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EarlyAccess = () => {
  const { referralCode } = useParams();
  const [step, setStep] = useState('form'); // form, success
  const [businessTypes, setBusinessTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    business_type: ''
  });
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchBusinessTypes();
    fetchStats();
  }, []);

  const fetchBusinessTypes = async () => {
    try {
      const response = await axios.get(`${API}/waitlist/business-types`);
      setBusinessTypes(response.data.business_types);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/waitlist/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.business_type) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const endpoint = referralCode 
        ? `${API}/waitlist/join/${referralCode}`
        : `${API}/waitlist/join`;
      
      const response = await axios.post(endpoint, formData);
      setResult(response.data);
      setStep('success');
      
      // Save to localStorage for returning users
      localStorage.setItem('waitlist_id', response.data.waitlist_id);
      localStorage.setItem('waitlist_referral', response.data.referral_code);
      
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    if (result?.referral_code) {
      const link = `${window.location.origin}/early-access/${result.referral_code}`;
      navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareLink = async () => {
    if (navigator.share && result) {
      try {
        await navigator.share({
          title: 'Join Clierxter Vision Lab Early Access',
          text: 'Transform your brand with AI-powered tools. Join the waitlist!',
          url: `${window.location.origin}/early-access/${result.referral_code}`
        });
      } catch (error) {
        copyReferralLink();
      }
    } else {
      copyReferralLink();
    }
  };

  const rewards = [
    { invites: 1, boost: 10, icon: ChevronUp },
    { invites: 3, boost: 30, icon: Zap },
    { invites: 5, boost: 50, icon: Star },
    { invites: 10, boost: 100, icon: Crown }
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-[#1C1C1C]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="group" data-testid="waitlist-logo">
            <span className="font-serif text-xl text-white tracking-wide">Clierxter</span>
            <span className="block text-[#D4AF37] text-[9px] font-accent tracking-[0.3em]">VISION LAB</span>
          </Link>
          <Link to="/introduction" className="text-[#A1A1AA] hover:text-white text-sm transition-colors">
            Learn More
          </Link>
        </div>
      </nav>

      <div className="pt-32 pb-24 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          {step === 'form' ? (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              {/* Header */}
              <div className="flex items-center justify-center gap-3 mb-8">
                <div className="w-12 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
                <span className="font-accent text-[#D4AF37] text-[10px] tracking-[0.3em]">EARLY ACCESS</span>
                <div className="w-12 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
              </div>

              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6">
                Clierxter Vision Lab is<br />
                <span className="text-[#E5C585]">Opening Soon</span>
              </h1>

              <p className="text-[#A1A1AA] text-lg md:text-xl max-w-2xl mx-auto mb-4 font-light">
                Join the Early Access list and be among the first to transform your vision into a powerful brand.
              </p>

              {referralCode && (
                <p className="text-[#D4AF37] text-sm mb-8">
                  You've been invited! Join now for priority access.
                </p>
              )}

              {/* Stats */}
              {stats && (
                <div className="flex justify-center gap-8 mb-12">
                  <div className="text-center">
                    <span className="font-serif text-3xl text-[#D4AF37]">{stats.total_signups || 0}</span>
                    <p className="text-[#52525B] text-sm">Already Joined</p>
                  </div>
                </div>
              )}

              {/* Form */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="max-w-md mx-auto"
              >
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="card-premium p-8">
                    <div className="space-y-4">
                      <div>
                        <Input
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Your Name"
                          className="input-underline text-center"
                          data-testid="waitlist-name"
                        />
                      </div>
                      
                      <div>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="Your Email"
                          className="input-underline text-center"
                          data-testid="waitlist-email"
                        />
                      </div>
                      
                      <div>
                        <select
                          value={formData.business_type}
                          onChange={(e) => setFormData({ ...formData, business_type: e.target.value })}
                          className="w-full bg-transparent border-b border-[#27272A] focus:border-[#D4AF37] py-3 text-white text-center outline-none appearance-none"
                          data-testid="waitlist-business-type"
                        >
                          <option value="" className="bg-[#050505]">Select Business Type</option>
                          {businessTypes.map(type => (
                            <option key={type} value={type} className="bg-[#050505]">{type}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      disabled={loading}
                      className="btn-gold w-full py-4 mt-6 flex items-center justify-center gap-2"
                      data-testid="waitlist-submit"
                    >
                      {loading ? (
                        'Joining...'
                      ) : (
                        <>
                          <Sparkles size={18} />
                          Join Early Access
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </motion.div>

              {/* Referral Rewards Preview */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mt-16"
              >
                <div className="flex items-center justify-center gap-3 mb-6">
                  <Gift className="text-[#D4AF37]" size={20} />
                  <span className="font-accent text-[#D4AF37] text-[10px] tracking-[0.25em]">MOVE UP THE LIST</span>
                </div>
                
                <p className="text-[#A1A1AA] text-sm mb-6">
                  Invite friends and move up the waitlist faster
                </p>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
                  {rewards.map((reward, index) => {
                    const Icon = reward.icon;
                    return (
                      <div key={index} className="card-luxury p-4 text-center">
                        <Icon className="text-[#D4AF37] mx-auto mb-2" size={24} />
                        <p className="text-white font-serif text-lg">{reward.invites} invite{reward.invites > 1 ? 's' : ''}</p>
                        <p className="text-[#E5C585] text-sm">+{reward.boost} positions</p>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            </motion.div>
          ) : (
            /* Success State */
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <div className="card-gold p-12 mb-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-radial from-[#D4AF37]/20 to-transparent rounded-full blur-3xl" />
                
                <div className="relative">
                  <Sparkles className="text-[#D4AF37] mx-auto mb-6" size={60} />
                  
                  <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
                    You're In!
                  </h2>
                  
                  <p className="text-[#A1A1AA] text-lg mb-8">
                    Welcome to the Clierxter Vision Lab Early Access list.
                  </p>

                  {/* Position Display */}
                  <div className="bg-[#0A0A0A] border border-[#D4AF37]/30 p-8 mb-8 inline-block">
                    <span className="text-[#D4AF37] text-[10px] font-accent tracking-wider">YOUR POSITION</span>
                    <div className="font-serif text-6xl text-white mt-2">#{result?.position}</div>
                  </div>

                  {result?.referred_by_name && (
                    <p className="text-[#E5C585] text-sm mb-6">
                      Referred by {result.referred_by_name}
                    </p>
                  )}
                </div>
              </div>

              {/* Referral Section */}
              <div className="card-premium p-8 mb-8">
                <div className="flex items-center justify-center gap-3 mb-6">
                  <Gift className="text-[#D4AF37]" size={20} />
                  <span className="font-accent text-[#D4AF37] text-[10px] tracking-[0.25em]">MOVE UP FASTER</span>
                </div>
                
                <h3 className="font-serif text-xl text-white mb-4">
                  Invite Friends, Move Up the List
                </h3>
                
                <p className="text-[#A1A1AA] text-sm mb-6">
                  For every friend who joins, you'll move up 10 positions.
                </p>

                <div className="bg-[#0A0A0A] border border-[#27272A] p-4 mb-4 flex items-center gap-3">
                  <div className="flex-1 text-[#E5C585] text-sm truncate">
                    {window.location.origin}/early-access/{result?.referral_code}
                  </div>
                  <Button onClick={copyReferralLink} className="btn-outline-gold px-4 py-2" data-testid="copy-waitlist-link">
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                  </Button>
                </div>

                <Button onClick={shareLink} className="btn-gold w-full py-4" data-testid="share-waitlist-link">
                  <Share2 size={18} className="mr-2" />
                  Share Your Link
                </Button>
              </div>

              {/* Rewards Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {rewards.map((reward, index) => {
                  const Icon = reward.icon;
                  return (
                    <div key={index} className="card-luxury p-4 text-center">
                      <Icon className="text-[#D4AF37] mx-auto mb-2" size={24} />
                      <p className="text-white text-sm">{reward.invites} invite{reward.invites > 1 ? 's' : ''}</p>
                      <p className="text-[#E5C585] text-xs">+{reward.boost} positions</p>
                    </div>
                  );
                })}
              </div>

              <p className="text-[#52525B] text-sm mt-8">
                We'll email you when it's your turn to join.
              </p>
            </motion.div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-[#1C1C1C]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#52525B] text-sm">
            © 2026 Clierxter Vision Lab. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default EarlyAccess;
